import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMedicineStoresData } from "../../services/Medicine/MedicineStore";
import { FaStore, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useLocationContext } from "../../context/LocationContext";

/* SWIPER */
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Navigation } from "swiper/modules";

export default function CommonHealthIssue() {
  const [stores, setStores] = useState([]);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const navigate = useNavigate();

  // ✅ USER LOCATION
  const { coords } = useLocationContext();

  // ================= DISTANCE =================
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;

    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // ================= FETCH =================
  useEffect(() => {
    const loadStores = async () => {
      try {
        console.log("🚀 FETCHING MEDICINE STORES...");

        const data = await fetchMedicineStoresData();

        console.log("✅ FULL API RESPONSE:", data);

        // ✅ SAFE ARRAY
        const allStores = Array.isArray(data) ? data : [];

        console.log("✅ TOTAL STORES:", allStores.length);

        // ✅ FILTER ONLY MEDICINE STORE
        const medicineStores = allStores.filter(
          (item) =>
            item.isMedicineStore === true ||
            item.isMedicineStore === "true"
        );

        console.log("✅ MEDICINE STORES:", medicineStores);

        // ✅ USER LOCATION
        console.log("📍 USER LOCATION:", coords);

        // ✅ FILTER BY DISTANCE
        const nearbyStores = medicineStores.filter((store, index) => {
          console.log(
            `================ STORE ${index + 1} ================`
          );

          console.log("🧪 COMPLETE STORE:", store);

          // ✅ HANDLE ALL POSSIBLE KEY NAMES
          const storeName =
            store.lab_name ||
            store.LabName ||
            store.name ||
            "Unknown Store";

          const storeLat = Number(
            store.latitude ||
              store.Latitude ||
              store.lat
          );

          const storeLng = Number(
            store.longnitude ||
              store.Longnitude ||
              store.longitude ||
              store.lng
          );

          console.log("🏪 STORE NAME:", storeName);
          console.log("📌 STORE LAT:", storeLat);
          console.log("📌 STORE LNG:", storeLng);

          console.log(
            "📌 RAW LAT VALUE:",
            store.latitude || store.Latitude
          );

          console.log(
            "📌 RAW LNG VALUE:",
            store.longnitude ||
              store.Longnitude ||
              store.longitude
          );

          // ✅ INVALID CHECK
          if (
            !coords?.lat ||
            !coords?.lng ||
            isNaN(storeLat) ||
            isNaN(storeLng)
          ) {
            console.log("❌ INVALID COORDS FOUND");
            return false;
          }

          // ✅ DISTANCE
          const distance = getDistanceKm(
            Number(coords.lat),
            Number(coords.lng),
            storeLat,
            storeLng
          );

          console.log(
            `📏 DISTANCE FROM USER TO ${storeName}:`,
            distance.toFixed(2),
            "KM"
          );

          // ✅ ONLY 20 KM
          const isNearby = distance <= 20;

          console.log(
            isNearby
              ? "✅ WITHIN 20 KM"
              : "❌ OUTSIDE 20 KM"
          );

          return isNearby;
        });

        console.log("🔥 FINAL NEARBY STORES:", nearbyStores);

        setStores(nearbyStores);
      } catch (err) {
        console.error("❌ FETCH ERROR:", err);
        setStores([]);
      }
    };

    // ✅ RUN ONLY WHEN LOCATION AVAILABLE
    if (coords?.lat && coords?.lng) {
      loadStores();
    }
  }, [coords]);

  // ================= LOCATION LOADING =================
  if (!coords?.lat || !coords?.lng) {
    return (
      <div className="text-center py-10 text-gray-500">
        📍 Detecting your live location...
      </div>
    );
  }

  // ================= EMPTY =================
  if (!stores || stores.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400">
        <FaStore
          size={50}
          className="mx-auto mb-3 opacity-50"
        />

        <p className="text-lg font-medium">
          No nearby medicine stores found
        </p>

        <p className="text-sm mt-2">
          Check console for debug logs
        </p>
      </div>
    );
  }

  return (
    <div className="relative overflow-x-visible px-4 md:px-8 py-6 bg-gray-50 rounded-xl shadow-lg mt-3">

      {/* TITLE */}
      <h4
        className="text-center text-2xl md:text-3xl font-extrabold mb-6
        bg-gradient-to-r from-purple-600 via-pink-500 to-red-500
        bg-clip-text text-transparent tracking-wide"
      >
        Nearby Medicine Stores
      </h4>

      {/* LEFT */}
      <button
        ref={prevRef}
        className="absolute left-0 md:left-2 top-1/2 -translate-y-1/2 z-10 bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center shadow-md hover:bg-red-700"
      >
        <FaChevronLeft size={14} />
      </button>

      {/* RIGHT */}
      <button
        ref={nextRef}
        className="absolute right-0 md:right-2 top-1/2 -translate-y-1/2 z-10 bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center shadow-md hover:bg-red-700"
      >
        <FaChevronRight size={14} />
      </button>

      {/* SWIPER */}
      <div className="px-6">
        <Swiper
          modules={[Navigation]}
          grabCursor={true}
          spaceBetween={16}
          slidesPerView={1}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl =
              prevRef.current;

            swiper.params.navigation.nextEl =
              nextRef.current;
          }}
          breakpoints={{
            576: {
              slidesPerView: 2,
            },
            768: {
              slidesPerView: 3,
            },
            1024: {
              slidesPerView: 4,
            },
          }}
        >
          {stores.map((store, index) => {
            const storeName =
              store.lab_name ||
              store.LabName ||
              store.name;

            const image =
              store.lab_image || store.image;

            return (
              <SwiperSlide
                key={store.id || index}
              >
                <div className="flex justify-center pb-3">

                  {/* CARD */}
                  <div className="w-full max-w-[260px] bg-white rounded-2xl shadow-md hover:shadow-xl transition duration-300 overflow-hidden hover:-translate-y-1">

                    {/* IMAGE */}
                    <div
                      className="h-[150px] bg-gray-100 flex items-center justify-center p-3 cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/get-medicine-by-store/${store.id}`
                        )
                      }
                    >
                    <img
  src={
    image ||
    "https://placehold.co/300x200?text=No+Image"
  }
  alt={storeName}
  loading="lazy"
  onError={(e) => {
    console.log("❌ IMAGE FAILED:", image);

    e.target.src =
      "https://placehold.co/300x200?text=No+Image";
  }}
  className="w-full h-full object-contain"
/>
                    </div>

                    {/* CONTENT */}
                    <div className="p-3 text-center">

                      <h6 className="font-semibold text-gray-800 truncate">
                        {storeName}
                      </h6>

                      {(store.phone_no ||
                        store.phone) && (
                        <p className="text-xs text-gray-500 mt-1">
                          {store.phone_no ||
                            store.phone}
                        </p>
                      )}

                      {store.address && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {store.address}
                        </p>
                      )}

                    </div>
                  </div>

                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
}